import { useEffect } from 'react';

// T125 — trap Tab focus inside an open overlay and restore focus to the trigger
// on close. Pass the panel element ref + whether the overlay is active. For
// always-mounted overlays (CommandPalette, ToastHistory) gate `active` on their
// open boolean so the focus-restore fires on close, not on real unmount.
// Optional `initialFocusRef` overrides the default "focus the first focusable"
// (use it where a specific field should get focus, e.g. a search input).
const SELECTOR =
  'a[href],button:not([disabled]),textarea:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])';

export function useFocusTrap(ref, active = true, initialFocusRef) {
  useEffect(() => {
    const node = ref.current;
    if (!active || !node) return undefined;

    const prev = document.activeElement;
    const focusables = () =>
      [...node.querySelectorAll(SELECTOR)].filter((el) => el.offsetParent !== null);

    const target = initialFocusRef?.current ?? focusables()[0] ?? node;
    target.focus?.();

    const onKey = (e) => {
      if (e.key !== 'Tab') return;
      const els = focusables();
      if (els.length === 0) {
        e.preventDefault();
        return;
      }
      const first = els[0];
      const last = els[els.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    node.addEventListener('keydown', onKey);
    return () => {
      node.removeEventListener('keydown', onKey);
      // Restore focus to whatever was focused before the overlay opened.
      prev?.focus?.();
    };
  }, [ref, active, initialFocusRef]);
}
